import React, { useEffect, useState } from "react";

import { classNames } from "../../utils";

type Props<T extends string> = {
  allowUnselect?: boolean;
  firstOptionSeparator?: React.ReactNode;
  multiple?: boolean;
  name?: string;
  onChange?: (selected: Array<T>) => void;
  options: Array<{
    content: React.ReactNode;
    id: T;
  }>;
  selected?: Array<T>;
  topOption?: null | T;
};

export default function VerticalOptions<T extends string>({
  allowUnselect,
  firstOptionSeparator,
  multiple,
  name,
  onChange,
  options,
  selected: propSelected,
  topOption,
}: Props<T>) {
  multiple = multiple ?? false;
  allowUnselect = allowUnselect ?? false;
  const [selected, setSelected] = useState<T[]>([]);
  const [changed, setChanged] = useState(false);

  function makeOnSelect(id: T) {
    return (evt: React.MouseEvent) => {
      evt.preventDefault();
      setChanged(true);

      if (selected.includes(id)) {
        if (allowUnselect || selected.length > 1) setSelected(selected.filter((s) => s !== id));
      } else if (multiple) {
        setSelected([...selected, id] as T[]);
      } else {
        setSelected([id] as T[]);
      }
    };
  }

  useEffect(() => {
    if (changed) onChange?.(selected);
  }, [selected, changed, onChange]);

  useEffect(() => {
    if (propSelected) setSelected(propSelected);
  }, [propSelected]);

  if (topOption) options = [...options].sort((a) => (a.id === topOption ? -1 : 1));

  return (
    <div className="space-y-3">
      <input name={name} type="hidden" value={selected.join(",")} />
      {options.map((option, index) => (
        <React.Fragment key={option.id}>
          <a
            className={classNames(
              "flex text-sm",
              `block cursor-pointer rounded-lg border dark:border-slate-700 bg-white hover:bg-gray-50 dark:bg-slate-800
              dark:hover:bg-slate-900 px-4 py-3 shadow-sm focus:outline-none sm:flex sm:justify-between`,
              selected.includes(option.id) &&
                "border-primary dark:border-primary-100 ring-1 dark:ring-2 ring-primary dark:ring-primary-100",
            )}
            onClick={makeOnSelect(option.id)}
          >
            {option.content}
          </a>
          {index === 0 && options.length > 0 && firstOptionSeparator}
        </React.Fragment>
      ))}
    </div>
  );
}

VerticalOptions.Icon = function Icon({ children }: { children: React.ReactNode }) {
  return <div className="flex shrink items-center pr-2">{children}</div>;
};

VerticalOptions.Left = function Left({ children }: { children: React.ReactNode }) {
  return <div className="grow">{children}</div>;
};
VerticalOptions.Right = function Right({ children }: { children: React.ReactNode }) {
  return <div className="text-right">{children}</div>;
};
VerticalOptions.Primary = function Secondary({ children }: { children: React.ReactNode }) {
  return <div className="font-semibold text-gray-800 dark:text-gray-200">{children}</div>;
};
VerticalOptions.Secondary = function Secondary({ children }: { children: React.ReactNode }) {
  return <div className="mt-1 font-normal text-gray-500">{children}</div>;
};
